import { LANES, type LaneId } from '@/lib/content-doctrine/lanes';

/**
 * Creative labeling — reading one finished creative and recording what it
 * actually does, on a fixed taxonomy, with evidence or nothing.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * This is the capability behind "reads every creative in your account". Mora
 * had no analogue: nothing anywhere labels a creative against a stable
 * vocabulary, so nothing can answer "what kind of work do we make?", "what have
 * we never tried?", or later "what do the ones that worked have in common?".
 *
 * It is also the ONE mechanism in this whole programme that needs no ad
 * delivery. It runs on pixels and copy, so it works on day zero, on an account
 * with no spend — which is exactly the condition the nearest competitor's
 * equivalent step fails on ("no connected ad account with performance history,
 * winners omitted"). That asymmetry is the opening, and it is why labeling is
 * built before any detector.
 *
 * ── THREE DELIBERATE DEPARTURES FROM THE COMPETITOR'S DESIGN ────────────────
 *
 * 1. **No 1-10 hook grade.** Theirs assigns one. Mora tried a centralised grade
 *    and a four-critic review killed it — a grade of your work reads as
 *    judgment ("I don't need another judge"), while a RANK among named others
 *    reads as competition. So this records WHICH hook mechanism is used and
 *    whether the creative evidences it; any comparison happens across the
 *    library, never as a mark out of ten on one asset.
 *
 * 2. **Angle reuses `LaneId`, it does not fork a parallel vocabulary.** The
 *    twelve lanes in `content-doctrine/lanes.ts` are already this codebase's
 *    argument taxonomy, already carry `bearsProof`/`bearsOpinion`, and already
 *    drive `slate.ts`'s balance rules. Labeling onto the same ids means a label
 *    can feed straight back into slate composition; a second vocabulary would
 *    have to be translated forever.
 *
 * 3. **Evidence or null, per field.** Every label carries the observation that
 *    supports it. A field with no supporting observation is `null`, never a
 *    guess — this is the same discipline `readObservedCreative` and the lens
 *    layer already hold, and the reason a labeling pass can be trusted as input
 *    to anything downstream.
 */

/** What job the opening move does. Mechanisms, not quality marks. */
export const HOOK_MECHANISMS = [
  'question',
  'claim',
  'contrast',
  'demonstration',
  'pattern_break',
  'callout',
  'story_open',
  'offer_led',
] as const;
export type HookMechanism = (typeof HOOK_MECHANISMS)[number];

/**
 * Whether the creative moves toward a wanted state or away from an unwanted
 * one. The single most useful axis for spotting a one-note library — an account
 * that is 100% desire-led has never tested the other half of its market.
 */
export const DESIRE_ORIENTATIONS = ['pain_led', 'desire_led', 'mixed'] as const;
export type DesireOrientation = (typeof DESIRE_ORIENTATIONS)[number];

/** What the creative asks the differentiator to do. */
export const USP_JOBS = [
  'new_mechanism',
  'better_execution',
  'lower_cost',
  'trust_transfer',
  'convenience',
  'none_stated',
] as const;
export type UspJob = (typeof USP_JOBS)[number];

export const MEDIA_FORMATS = ['static', 'video', 'carousel', 'ugc', 'text_only'] as const;
export type MediaFormat = (typeof MEDIA_FORMATS)[number];

/**
 * One labelled field: the value, plus the observation in the creative that
 * supports it. `evidence: null` means the labeller could not point at anything
 * — and in that case `value` MUST be null too. `isLabelWellFormed` enforces it.
 */
export interface LabeledField<T> {
  value: T | null;
  /** What in the creative supports this — quoted copy, or a described element. */
  evidence: string | null;
}

export interface CreativeLabel {
  /** Two-to-five words, in the labeller's own words. Not from a fixed list. */
  theme: LabeledField<string>;
  /** The argument this creative makes, on the shared lane vocabulary. */
  lane: LabeledField<LaneId>;
  hookMechanism: LabeledField<HookMechanism>;
  desireOrientation: LabeledField<DesireOrientation>;
  uspJob: LabeledField<UspJob>;
  mediaFormat: LabeledField<MediaFormat>;
  /** Whether a concrete offer is present (a price, a discount, a guarantee). */
  offerPresent: LabeledField<boolean>;
}

export const LABEL_FIELDS = [
  'theme',
  'lane',
  'hookMechanism',
  'desireOrientation',
  'uspJob',
  'mediaFormat',
  'offerPresent',
] as const satisfies readonly (keyof CreativeLabel)[];

/**
 * The evidence-or-null invariant, as a checkable rule rather than a comment.
 *
 * A value without evidence is exactly the failure this taxonomy exists to
 * prevent: a confident label nobody can trace back to the creative. Returns the
 * offending field names so a caller can reject a bad labelling run rather than
 * storing it.
 */
export function unevidencedFields(label: CreativeLabel): string[] {
  return LABEL_FIELDS.filter((field) => {
    const entry = label[field] as LabeledField<unknown>;
    return entry.value !== null && (entry.evidence === null || entry.evidence.trim() === '');
  });
}

export function isLabelWellFormed(label: CreativeLabel): boolean {
  return unevidencedFields(label).length === 0;
}

/** How much of the taxonomy this creative could actually be labelled on, 0–1. */
export function labelCoverage(label: CreativeLabel): number {
  const filled = LABEL_FIELDS.filter(
    (field) => (label[field] as LabeledField<unknown>).value !== null,
  ).length;
  return filled / LABEL_FIELDS.length;
}

/**
 * ── THE MORA-ONLY MOVE ──────────────────────────────────────────────────────
 * Mora AUTHORED many of these creatives, so it knows what the Brief intended.
 * A competitor labelling an asset it did not make can only report what it sees;
 * Mora can report the GAP between what was briefed and what was produced.
 *
 * "Briefed as `objection_head_on`, reads as `proof_in_public`" is a finding no
 * tool without the Brief can produce, and it is actionable in a way a bare
 * label is not.
 *
 * Returns `null` when there is nothing to compare — no intent on file, or the
 * lane could not be labelled. Silence is correct there; a "matches" verdict
 * computed from a missing operand would be the false-zero this repo bans.
 */
export interface IntentDivergence {
  intended: LaneId;
  perceived: LaneId;
  /** Present so a caller can show WHY the perceived lane was read that way. */
  evidence: string | null;
  sentence: string;
}

export function compareIntentToLabel(
  intendedLane: LaneId | null | undefined,
  label: CreativeLabel,
): IntentDivergence | null {
  const perceived = label.lane.value;
  if (!intendedLane || !perceived) return null;
  if (intendedLane === perceived) return null;
  return {
    intended: intendedLane,
    perceived,
    evidence: label.lane.evidence,
    sentence: `Briefed as ${LANES[intendedLane].label}, reads as ${LANES[perceived].label}.`,
  };
}

/**
 * Library composition — the read that is useful before any ad has ever run.
 *
 * Counts by value across labelled creatives, and names the taxonomy values that
 * appear ZERO times. The gaps are the point: "0 of 41 creatives are pain-led"
 * is a content-strategy finding available on an account with no spend at all.
 */
export interface CompositionRead<T extends string> {
  counts: Record<string, number>;
  /** Values in the taxonomy that no labelled creative used. */
  untried: T[];
  /** How many creatives carried a value for this field. */
  labelled: number;
}

function compositionOf<T extends string>(
  labels: readonly CreativeLabel[],
  field: keyof CreativeLabel,
  universe: readonly T[],
): CompositionRead<T> {
  const counts: Record<string, number> = {};
  let labelled = 0;
  for (const label of labels) {
    const value = (label[field] as LabeledField<unknown>).value;
    if (value === null) continue;
    labelled += 1;
    const key = String(value);
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return { counts, untried: universe.filter((v) => !counts[v]), labelled };
}

export function libraryComposition(labels: readonly CreativeLabel[]) {
  return {
    lane: compositionOf(labels, 'lane', Object.keys(LANES) as LaneId[]),
    hookMechanism: compositionOf(labels, 'hookMechanism', HOOK_MECHANISMS),
    desireOrientation: compositionOf(labels, 'desireOrientation', DESIRE_ORIENTATIONS),
    uspJob: compositionOf(labels, 'uspJob', USP_JOBS),
    mediaFormat: compositionOf(labels, 'mediaFormat', MEDIA_FORMATS),
  };
}
